import { useCallback, useMemo, useState } from 'react';

import { downloadCopilotArtifact } from 'lib/services/copilotAgents';

const initialViewerState = {
    isOpen: false,
    artifact: null,
    content: null,
    loading: false,
    error: null,
};

/**
 * Owns the viewer modal state and the default per-`artifactType` handlers used
 * by the Copilot context. Kept outside `Context.jsx` so adding new artifact
 * types only requires touching this module (and a service for the download).
 *
 * Returns:
 * - `artifactHandlers`: current map of `artifactType` → handler.
 * - `registerArtifactHandlers(handlers)`: merge custom handlers (overrides
 *   defaults).
 * - `viewerState` / `closeArtifactViewer`: state and dismiss action consumed
 *   by `<ArtifactViewer />`.
 * - `openArtifactViewer(artifact)`: imperative API exposed via context for
 *   custom handlers that want to reuse the modal.
 */
const useArtifactHandlers = () => {
    const [viewerState, setViewerState] = useState(initialViewerState);

    const closeArtifactViewer = useCallback(() => {
        setViewerState(initialViewerState);
    }, []);

    const openArtifactViewer = useCallback((artifact) => {
        if (!artifact?.download) return;
        setViewerState({
            isOpen: true,
            artifact,
            content: null,
            loading: true,
            error: null,
        });
        downloadCopilotArtifact(artifact.download)
            .then((content) => {
                setViewerState((prev) => {
                    if (!prev.isOpen || prev.artifact !== artifact) return prev;
                    return { ...prev, content, loading: false };
                });
            })
            .catch((err) => {
                console.error('Error downloading Copilot artifact:', err);
                setViewerState((prev) => {
                    if (!prev.isOpen || prev.artifact !== artifact) return prev;
                    return {
                        ...prev,
                        loading: false,
                        error: err?.message || 'Error',
                    };
                });
            });
    }, []);

    /**
     * Default handlers per `artifactType`. Only built-in handlers (rendered by
     * web5 itself) live here. Host-app handlers (e.g. `dashboard`,
     * `form_entity` in ssm-web4) must register through
     * `registerArtifactHandlers`. Unknown types fall back to
     * `defaultArtifactHandler` in `Context.jsx`.
     */
    const defaultHandlers = useMemo(
        () => ({
            markdown: openArtifactViewer,
        }),
        [openArtifactViewer],
    );

    const [overrides, setOverrides] = useState({});

    const registerArtifactHandlers = useCallback((handlers = {}) => {
        setOverrides((prev) => ({ ...prev, ...handlers }));
    }, []);

    const artifactHandlers = useMemo(
        () => ({ ...defaultHandlers, ...overrides }),
        [defaultHandlers, overrides],
    );

    return {
        artifactHandlers,
        registerArtifactHandlers,
        viewerState,
        openArtifactViewer,
        closeArtifactViewer,
    };
};

export default useArtifactHandlers;
